// Sampled General MIDI sounds for the score player, in the browser.
// The samples are the FluidR3 General MIDI soundfont as MIDI.js files (one per instrument,
// every note A0 to C8 as base64 MP3), from Benjamin Gleitz's midi-js-soundfonts on GitHub
// Pages; the same files smplr and soundfont-player load. An instrument's file is fetched once,
// then only the notes a passage needs are decoded.

const BASE = 'https://gleitz.github.io/midi-js-soundfonts/FluidR3_GM/';
// Sounds that hold as long as the note: their sample is looped past its attack.
const HELD = new Set(['flute', 'piccolo', 'oboe', 'clarinet', 'bassoon', 'french_horn', 'trumpet', 'trombone', 'tuba',
  'violin', 'viola', 'cello', 'contrabass', 'string_ensemble_1', 'choir_aahs', 'church_organ']);
// A rough balance between the samples, which come at very different loudness.
const LEVEL: Record<string, number> = {
  choir_aahs: 0.9, church_organ: 0.45, orchestral_harp: 1.2, timpani: 1.1, french_horn: 0.9,
  trombone: 0.75, trumpet: 0.6, contrabass: 1.1, acoustic_grand_piano: 0.8,
};

const PC: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
function nameToMidi(n: string): number | null {
  const m = /^([A-G])(b?)(-?\d)$/.exec(n);
  if (!m) return null;
  return (Number(m[3]) + 1) * 12 + PC[m[1]] - (m[2] ? 1 : 0);
}
function b64ToBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64), out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

export interface Voice { src: AudioBufferSourceNode; gain: GainNode; tag: string; end: number }

export class Sampler {
  ctx: AudioContext;
  out: GainNode;
  private banks = new Map<string, Promise<Map<number, string>>>();
  private buffers = new Map<string, AudioBuffer>();
  private decoding = new Map<string, Promise<AudioBuffer | null>>();
  voices = new Set<Voice>();

  constructor() {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new AC();
    const comp = this.ctx.createDynamicsCompressor();
    comp.threshold.value = -14; comp.ratio.value = 4;
    this.out = this.ctx.createGain();
    this.out.gain.value = 0.55;
    this.out.connect(comp); comp.connect(this.ctx.destination);
  }

  private bank(patch: string): Promise<Map<number, string>> {
    let p = this.banks.get(patch);
    if (!p) {
      p = fetch(`${BASE}${patch}-mp3.js`).then(r => {
        if (!r.ok) throw new Error(`The ${patch.replace(/_/g, ' ')} sound could not be fetched.`);
        return r.text();
      }).then(text => {
        const map = new Map<number, string>();
        const re = /"([A-G]b?-?\d)":\s*"data:audio\/mp3;base64,([^"]+)"/g;
        let m: RegExpExecArray | null;
        while ((m = re.exec(text))) { const k = nameToMidi(m[1]); if (k != null) map.set(k, m[2]); }
        return map;
      });
      p.catch(() => this.banks.delete(patch));
      this.banks.set(patch, p);
    }
    return p;
  }

  has(patch: string, midi: number) { return this.buffers.has(`${patch}:${midi}`); }

  private decode(patch: string, midi: number): Promise<AudioBuffer | null> {
    const key = `${patch}:${midi}`;
    if (this.buffers.has(key)) return Promise.resolve(this.buffers.get(key) as AudioBuffer);
    let p = this.decoding.get(key);
    if (!p) {
      p = this.bank(patch).then(async map => {
        const b64 = map.get(Math.max(21, Math.min(108, midi)));
        if (!b64) return null;
        const buf = await this.ctx.decodeAudioData(b64ToBuffer(b64));
        this.buffers.set(key, buf);
        return buf;
      }).catch(() => null);
      this.decoding.set(key, p);
    }
    return p;
  }

  // Make ready every (sound, pitch) a passage needs. onProgress gets (sounds ready, sounds wanted).
  async prepare(pairs: Array<[string, number]>, onProgress?: (done: number, all: number) => void) {
    const want = pairs.filter(([p, m]) => !this.has(p, m));
    const patches = Array.from(new Set(want.map(([p]) => p)));
    let done = 0;
    onProgress?.(0, patches.length);
    await Promise.all(patches.map(async p => {
      try { await this.bank(p); } catch { /* reported by the caller as a missing sound */ }
      done++; onProgress?.(done, patches.length);
    }));
    for (let i = 0; i < want.length; i += 24) await Promise.all(want.slice(i, i + 24).map(([p, m]) => this.decode(p, m)));
  }

  // Sound one note. Returns false when its sample is not ready yet (it is then fetched for next time).
  play(patch: string, midi: number, when: number, dur: number, vel: number, tag: string): boolean {
    const buf = this.buffers.get(`${patch}:${midi}`);
    if (!buf) { this.decode(patch, midi); return false; }
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const held = HELD.has(patch);
    if (held && dur > buf.duration - 0.6) {
      src.loop = true;
      src.loopStart = Math.min(0.8, buf.duration * 0.3);
      src.loopEnd = Math.max(src.loopStart + 0.3, buf.duration - 0.5);
    }
    const g = ctx.createGain();
    const peak = Math.pow(Math.max(1, vel) / 127, 1.4) * (LEVEL[patch] ?? 0.8);
    const t = Math.max(when, ctx.currentTime);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(peak, t + (held ? 0.05 : 0.005));
    const off = when + dur;
    const tau = held ? 0.09 : 0.35;
    g.gain.setValueAtTime(peak, Math.max(t + 0.05, off));
    g.gain.setTargetAtTime(0, Math.max(t + 0.05, off), tau);
    src.connect(g); g.connect(this.out);
    src.start(t, Math.max(0, t - when) > 0 && !src.loop ? Math.min(buf.duration - 0.05, t - when) : 0);
    const end = Math.max(t + 0.06, off) + tau * 6;
    src.stop(end);
    const v: Voice = { src, gain: g, tag, end };
    this.voices.add(v);
    src.onended = () => { this.voices.delete(v); try { g.disconnect(); } catch { /* already gone */ } };
    return true;
  }

  // Fade out and stop every sounding note, or only those whose tag passes the test.
  hush(test?: (tag: string) => boolean) {
    const now = this.ctx.currentTime;
    this.voices.forEach(v => {
      if (test && !test(v.tag)) return;
      try {
        v.gain.gain.cancelScheduledValues(now);
        v.gain.gain.setValueAtTime(v.gain.gain.value, now);
        v.gain.gain.setTargetAtTime(0, now, 0.04);
        v.src.stop(now + 0.25);
      } catch { /* already stopped */ }
      this.voices.delete(v);
    });
  }
}
